import React, { useState } from "react";
import { motion } from "framer-motion";
import { Plane, MessageCircle, CheckCircle2, Phone, Users } from "lucide-react";
import api from "../lib/api";
import { useApp } from "../context/AppContext";
import DateRangePicker from "../components/DateRangePicker";
import PageTransition from "../components/PageTransition";
import { toast } from "sonner";

const todayISO = () => new Date().toISOString().slice(0, 10);
const addDays = (iso, n) => { const d = new Date(iso); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10); };

export default function Flights() {
    const { lang } = useApp();
    const [form, setForm] = useState({
        customer_name: "", customer_phone: "",
        from_city: "İstanbul", to_city: "Cidde",
        date_from: todayISO(), date_to: addDays(todayISO(), 14),
        persons: 2, notes: "",
    });
    const [submitting, setSubmitting] = useState(false);
    const [result, setResult] = useState(null);

    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            const { data } = await api.post("/service-requests", { service_type: "flight", ...form });
            setResult(data);
            toast.success("Talebiniz alındı! En kısa sürede size dönüş yapacağız.");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Talep gönderilemedi");
        } finally { setSubmitting(false); }
    };

    return (
        <PageTransition>
            <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16" data-testid="flights-page">
                <div className="text-center mb-10">
                    <div className="text-xs uppercase tracking-[0.35em] text-gold mb-3">UÇAK BİLETİ</div>
                    <h1 className="font-serif text-4xl sm:text-5xl text-bone tracking-tight">Umre Uçak Biletleri</h1>
                    <p className="text-bone/70 mt-4 max-w-2xl mx-auto leading-relaxed">
                        İstanbul, Ankara ve tüm Türkiye'den Cidde ve Medine'ye en uygun uçak biletleri.
                        <span className="text-gold font-semibold"> Bilet fiyatları için bizimle iletişime geçiniz</span> — istediğiniz tarihi ve kişi sayısını seçin, size özel fiyat sunalım.
                    </p>
                </div>

                {result ? (
                    <div className="border border-gold/40 bg-emerald_surface p-8 text-center space-y-4" data-testid="flight-success">
                        <CheckCircle2 className="w-12 h-12 text-gold mx-auto" />
                        <h2 className="font-serif text-2xl">Talebiniz Alındı!</h2>
                        <p className="text-muted_green text-sm max-w-md mx-auto">Ekibimiz en uygun bilet fiyatlarıyla en kısa sürede size dönüş yapacak. Daha hızlı yanıt için WhatsApp'tan da ulaşabilirsiniz.</p>
                        {result.whatsapp_url && (
                            <a href={result.whatsapp_url} target="_blank" rel="noreferrer" className="btn-gold !text-sm inline-flex" data-testid="flight-whatsapp-btn">
                                <MessageCircle className="w-4 h-4" /> WhatsApp'tan Yaz
                            </a>
                        )}
                        <div>
                            <button onClick={() => setResult(null)} className="text-xs text-muted_green underline hover:text-gold mt-2">Yeni talep oluştur</button>
                        </div>
                    </div>
                ) : (
                    <motion.form
                        onSubmit={submit}
                        initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
                        className="border border-gold/40 bg-emerald_surface p-6 lg:p-8 space-y-4"
                        data-testid="flight-form"
                    >
                        <div className="flex items-center gap-2 text-gold">
                            <Plane className="w-5 h-5" />
                            <h2 className="font-serif text-2xl text-bone">Bilet Talebi Oluştur</h2>
                        </div>
                        <div className="grid sm:grid-cols-2 gap-4">
                            <div>
                                <label className="label-dark">Nereden</label>
                                <input type="text" required className="input-dark" value={form.from_city} onChange={(e) => setForm({ ...form, from_city: e.target.value })} placeholder="İstanbul" data-testid="flight-from" />
                            </div>
                            <div>
                                <label className="label-dark">Nereye</label>
                                <select className="input-dark" value={form.to_city} onChange={(e) => setForm({ ...form, to_city: e.target.value })} data-testid="flight-to">
                                    <option value="Cidde" className="bg-emerald_surface">Cidde (Mekke)</option>
                                    <option value="Medine" className="bg-emerald_surface">Medine</option>
                                    <option value="Riyad" className="bg-emerald_surface">Riyad</option>
                                </select>
                            </div>
                        </div>
                        <div>
                            <label className="label-dark">Gidiş — Dönüş Tarihi</label>
                            <div className="drp-in-form">
                                <DateRangePicker
                                    variant="dark"
                                    lang={lang}
                                    value={{ from: form.date_from, to: form.date_to }}
                                    onChange={({ from, to }) => setForm((f) => ({ ...f, date_from: from || f.date_from, date_to: to || f.date_to }))}
                                    testIds={{ cellIn: "flight-date-from-cell", cellOut: "flight-date-to-cell", checkIn: "flight-date-from", checkOut: "flight-date-to" }}
                                    labels={{ checkIn: "Gidiş", checkOut: "Dönüş" }}
                                />
                            </div>
                        </div>
                        <div className="grid sm:grid-cols-2 gap-4">
                            <div>
                                <label className="label-dark"><Users className="w-3 h-3 inline mr-1" /> Kişi Sayısı</label>
                                <input type="number" min={1} max={100} required className="input-dark" value={form.persons} onChange={(e) => setForm({ ...form, persons: Number(e.target.value) })} data-testid="flight-persons" />
                            </div>
                            <div>
                                <label className="label-dark">Ad Soyad</label>
                                <input type="text" required className="input-dark" value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} data-testid="flight-name" />
                            </div>
                        </div>
                        <div>
                            <label className="label-dark"><Phone className="w-3 h-3 inline mr-1" /> Telefon / WhatsApp</label>
                            <input type="tel" required className="input-dark" value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} placeholder="+90 5xx xxx xx xx" data-testid="flight-phone" />
                        </div>
                        <div>
                            <label className="label-dark">Not (opsiyonel)</label>
                            <textarea rows={2} className="input-dark" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Direkt uçuş, bagaj, aktarma tercihi..." data-testid="flight-notes" />
                        </div>
                        <button type="submit" disabled={submitting} className="btn-gold w-full !py-3.5" data-testid="flight-submit-btn">
                            <MessageCircle className="w-4 h-4" /> {submitting ? "Gönderiliyor…" : "Fiyat İçin Bizimle İletişime Geçin"}
                        </button>
                    </motion.form>
                )}
            </section>
        </PageTransition>
    );
}
